import { ChangeDetectionStrategy, Component, computed, Input, OnInit, inject, signal } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { AsinService, InventoryService } from "@front/m19-services";
import { combineLatest } from "rxjs";
import { Marketplace } from "@front/m19-api-client";
import { IAlertComponent } from "@front/m19-ui";

@UntilDestroy()
@Component({
  selector: "app-low-inventory-warning",
  template: ` @if (pausedAsins().length > 0) {
    <IAlert type="warning">{{ pausedMsg() }}</IAlert>
  }`,
  imports: [IAlertComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LowInventoryWarningComponent implements OnInit {
  private asinService = inject(AsinService);
  private inventoryService = inject(InventoryService);

  @Input()
  accountId!: string;

  @Input()
  marketplace!: Marketplace;

  @Input()
  asins!: string[];

  pausedAsins = signal<string[]>([]);
  pausedMsg = computed(() => this.getPausedMsg(this.pausedAsins()));

  ngOnInit(): void {
    combineLatest([
      this.inventoryService.getSellerInventoryStats(this.accountId, this.marketplace),
      this.asinService.getInventoryRules(this.accountId, this.marketplace),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([inventoryStats, inventoryRules]) => {
        const asinsSet = new Set(this.asins);
        this.pausedAsins.set(
          inventoryStats
            .filter(
              (stat) => asinsSet.has(stat.asin) && inventoryRules?.execute(stat.asin, stat).shouldPauseAdvertising,
            )
            .map((stat) => stat.asin),
        );
      });
  }

  private getPausedMsg(pausedAsins: string[]) {
    return (
      `${pausedAsins.length} ` +
      (pausedAsins.length > 1 ? "ASINs have" : "ASIN has") +
      " been automatically paused because of low inventory: " +
      this.prettyPrintAsinList(pausedAsins)
    );
  }

  private prettyPrintAsinList(asins: string[]) {
    const maxAsins = 5;
    let message = asins.slice(0, maxAsins).join(", ");
    if (asins.length > maxAsins) {
      message += "…";
    }
    return message;
  }
}
